/**
 * THOR toast — react-hot-toast shaped API with polished royal cards.
 */
(() => {
  if (window.thorToast) return;

  const LABELS = {
    success: 'Success',
    remove: 'Removed',
    error: 'Error',
    blank: 'Notice',
    loading: 'Please wait',
  };

  const ICONS = {
    success:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>',
    remove:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6"/></svg>',
    error:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M15 9l-6 6M9 9l6 6"/></svg>',
    blank:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16h.01"/></svg>',
    loading:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M12 3a9 9 0 1 1-6.36 2.64"/></svg>',
  };

  const CLOSE_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  const ensureRoot = () => {
    let root = document.querySelector('.thor-toast-root');
    if (!root) {
      root = document.createElement('div');
      root.className = 'thor-toast-root';
      root.dataset.position = 'top-center';
      root.setAttribute('aria-live', 'polite');
      document.body.appendChild(root);
    }
    return root;
  };

  const clearTimer = (el) => {
    if (el._thorTimer) {
      clearTimeout(el._thorTimer);
      el._thorTimer = null;
    }
  };

  const dismiss = (id) => {
    const el = document.getElementById(id);
    if (!el || el.classList.contains('is-leaving')) return;
    clearTimer(el);
    el.classList.add('is-leaving');
    const done = () => el.remove();
    el.addEventListener('animationend', done, { once: true });
    setTimeout(done, 320);
  };

  const bindHoverPause = (el, id, duration) => {
    if (duration === Infinity) return;

    const start = () => {
      clearTimer(el);
      const progress = el.querySelector('.thor-toast__progress');
      const remaining = el._thorRemaining ?? duration;
      const startedAt = performance.now();
      el._thorStartedAt = startedAt;
      el._thorRemaining = remaining;
      if (progress) {
        progress.style.animationDuration = `${remaining}ms`;
        progress.style.animationName = 'none';
        // restart progress for remaining time
        void progress.offsetWidth;
        progress.style.animationName = 'thor-toast-progress';
        progress.style.animationDuration = `${remaining}ms`;
      }
      el._thorTimer = setTimeout(() => dismiss(id), remaining);
    };

    const pause = () => {
      if (!el._thorStartedAt) return;
      const elapsed = performance.now() - el._thorStartedAt;
      el._thorRemaining = Math.max(0, (el._thorRemaining ?? duration) - elapsed);
      clearTimer(el);
    };

    el.addEventListener('mouseenter', pause);
    el.addEventListener('focusin', pause);
    el.addEventListener('mouseleave', start);
    el.addEventListener('focusout', (e) => {
      if (!el.contains(e.relatedTarget)) start();
    });

    el._thorRemaining = duration;
    start();
  };

  const render = (el, message, type, duration) => {
    const label = LABELS[type] || LABELS.blank;
    el.className = `thor-toast thor-toast--${type}`;
    el.innerHTML = `
      <span class="thor-toast__icon-wrap" aria-hidden="true">${ICONS[type] || ICONS.blank}</span>
      <div class="thor-toast__body">
        <p class="thor-toast__label">${label}</p>
        <p class="thor-toast__message"></p>
      </div>
      <button type="button" class="thor-toast__close" aria-label="Dismiss">${CLOSE_ICON}</button>
      ${duration !== Infinity ? '<span class="thor-toast__progress" aria-hidden="true"></span>' : ''}
    `;
    el.querySelector('.thor-toast__message').textContent = message;
    el.querySelector('.thor-toast__close').addEventListener('click', () => dismiss(el.id));
  };

  const show = (message, opts = {}) => {
    const type = opts.type || 'blank';
    const duration = opts.duration ?? (type === 'loading' ? Infinity : 3800);
    const id = opts.id || `thor-toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const root = ensureRoot();

    let el = document.getElementById(id);
    const isUpdate = Boolean(el);
    if (!el) {
      el = document.createElement('div');
      el.id = id;
      el.setAttribute('role', type === 'error' || type === 'remove' ? 'alert' : 'status');
      root.appendChild(el);
    } else {
      el.classList.remove('is-leaving');
      clearTimer(el);
    }

    render(el, message, type, duration);

    if (isUpdate) {
      el.style.animation = 'none';
      void el.offsetWidth;
      el.style.animation = '';
    }

    if (duration !== Infinity) {
      const progress = el.querySelector('.thor-toast__progress');
      if (progress) progress.style.animationDuration = `${duration}ms`;
      bindHoverPause(el, id, duration);
    }

    return id;
  };

  const toast = (message, opts) => show(message, { ...opts, type: opts?.type || 'blank' });
  toast.success = (message, opts) => show(message, { ...opts, type: 'success' });
  toast.remove = (message, opts) => show(message, { ...opts, type: 'remove' });
  toast.error = (message, opts) => show(message, { ...opts, type: 'error' });
  toast.loading = (message, opts) => show(message, { ...opts, type: 'loading', duration: Infinity });
  toast.dismiss = dismiss;
  toast.promise = (promise, messages = {}) => {
    const id = toast.loading(messages.loading || 'Please wait…');
    return Promise.resolve(promise)
      .then((value) => {
        toast.success(
          typeof messages.success === 'function' ? messages.success(value) : messages.success || 'Done',
          { id }
        );
        return value;
      })
      .catch((err) => {
        toast.error(
          typeof messages.error === 'function' ? messages.error(err) : messages.error || 'Something went wrong',
          { id }
        );
        throw err;
      });
  };

  window.thorToast = toast;
  window.toast = toast;
})();
